import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, } from '@angular/router';
import { Store } from '@ngrx/store';
import { initializeSession, selectAuthStatus, selectUser, setReturnUrl, State, } from '@lmc-app/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthStatus } from '@lmc-app/shared/enum/auth-status.enum';
import { of } from 'rxjs';

/**
 * Auth Guard
 *
 * Protects routes by checking if user is authenticated.
 * Handles session initialization automatically on first access.
 *
 * Design Decision:
 * - Session is initialized ONCE on first protected route access
 * - Subsequent route activations use cached user state from NgRx store
 * - No API call on every route change (better performance)
 * - Token expiration is handled by auth-error.interceptor on any API call
 * - If token expires, interceptor will automatically refresh or logout
 *
 * Flow:
 * 1. Check authStatus from NgRx store
 * 2. If UNKNOWN (app just started), dispatch initializeSession() and wait
 * 3. If LOADING (initialization in progress), wait for completion
 * 4. If AUTHENTICATED, allow access
 * 5. All other statuses (UNAUTHENTICATED, SESSION_EXPIRED, ERROR) → redirect to /login
 *
 * AuthStatus State Transitions:
 * - UNKNOWN: Initial state, haven't checked yet
 * - LOADING: Calling /v2/users/me or login in progress
 * - AUTHENTICATED: Login success OR initializeSession success OR refreshToken success
 * - UNAUTHENTICATED: initializeSession failed OR login failed OR user logout OR refreshToken failed
 * - SESSION_EXPIRED: (Reserved, not currently used)
 * - ERROR: API errors during authentication
 *
 * Advantages:
 * - Minimal API calls (only once per app session)
 * - Fast route transitions (no waiting for API)
 * - Better user experience
 * - HttpOnly cookies are validated on every API request anyway
 * - Interceptor handles 401 errors automatically
 * - Replaces duplicate session initialization in app.component
 *
 * Security:
 * - Fail-safe design: Only AUTHENTICATED explicitly allows access
 * - All other statuses require login (secure by default)
 * - No localStorage dependencies
 */
export const authGuard: CanActivateFn = (
	_route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot
) => {
	const router = inject(Router);
	const store = inject(Store<State>);
	return store.select(selectAuthStatus).pipe(
		take(1),
		switchMap((authStatus) => {
			// If session not initialized yet (app just started), initialize it
			if (authStatus === AuthStatus.UNKNOWN) {
				store.dispatch(initializeSession());

				// Wait for initialization to complete
				return store.select(selectAuthStatus).pipe(
					filter(
						(status) =>
							status !== AuthStatus.UNKNOWN && status !== AuthStatus.LOADING
					),
					take(1)
				);
			}

			// If currently loading (another route triggered initialization), wait for it
			if (authStatus === AuthStatus.LOADING) {
				return store.select(selectAuthStatus).pipe(
					filter((status) => status !== AuthStatus.LOADING),
					take(1)
				);
			}

			// Already initialized, return current status immediately
			return of(authStatus);
		}),
		switchMap((authStatus) => {
			// Now check if user is authenticated
			return store.select(selectUser).pipe(
				take(1),
				map((user) => {
					// Only allow access if explicitly AUTHENTICATED with valid user
					if (authStatus === AuthStatus.AUTHENTICATED && user) {
						return true;
					}

					// All other cases: redirect to login
					// (UNAUTHENTICATED, SESSION_EXPIRED, ERROR, or no user data)

					// Store the return URL in NgRx state for post-login redirect
					console.log('[AuthGuard] Storing returnUrl:', state.url);
					store.dispatch(setReturnUrl({ returnUrl: state.url }));

					// Redirect to login with returnUrl as query param (for backwards compatibility)
					void router.navigate(['login']);
					return false;
				})
			);
		})
	);
};
